import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CaretDown, TrendUp, WarningCircle, Lightning } from "@phosphor-icons/react";

export const PRIORITY_STYLE = {
  very_high: { dot: "#059669", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", ring: "#059669" },
  high: { dot: "#C5A059", chip: "bg-[#C5A059]/10 text-[#8a6d2f] border-[#C5A059]/30", ring: "#C5A059" },
  medium: { dot: "#2563eb", chip: "bg-blue-50 text-blue-700 border-blue-200", ring: "#2563eb" },
  low: { dot: "#64748b", chip: "bg-slate-100 text-slate-600 border-slate-200", ring: "#94a3b8" },
};

function factorLabel(t, f) {
  return t(`scoring.factor.${f.code}`, { ...(f.params || {}), defaultValue: f.code });
}

export function ScoreRing({ value, priority = "low", size = 44 }) {
  const style = PRIORITY_STYLE[priority] || PRIORITY_STYLE.low;
  const r = size / 2 - 3;
  const c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, value)) / 100) * c;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }} data-testid="score-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef2f6" strokeWidth="3" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={style.ring} strokeWidth="3"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-serif text-sm text-slate-800">{value}</span>
    </div>
  );
}

export function PriorityChip({ priority }) {
  const { t } = useTranslation();
  const style = PRIORITY_STYLE[priority] || PRIORITY_STYLE.low;
  return (
    <span data-testid={`priority-chip-${priority}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border rounded-sm ${style.chip}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: style.dot }} />
      {t(`scoring.priority.${priority}`, priority)}
    </span>
  );
}

/** Compact inline score chip (ring + priority) for cards. */
export function ScoreChip({ score, testid }) {
  if (!score) return null;
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <ScoreRing value={score.overall} priority={score.priority} size={38} />
      <PriorityChip priority={score.priority} />
    </div>
  );
}

/** Expandable "Why this score?" panel showing factors, risks and next action. */
export function WhyScore({ score, testid = "why-score" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!score) return null;
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`}
        className="flex items-center gap-1 text-xs font-medium text-[#0B2545] hover:text-[#C5A059] transition-colors">
        <Lightning size={13} weight="fill" className="text-[#C5A059]" />
        {t("scoring.whyScore", "Why this score?")}
        <CaretDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-3 border-t border-slate-100 pt-3" data-testid={`${testid}-panel`}>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><div className="text-slate-400 uppercase tracking-wide text-[10px]">{t("scoring.dealProbability", "Deal probability")}</div><div className="font-semibold text-slate-800">{score.deal_probability}%</div></div>
            <div><div className="text-slate-400 uppercase tracking-wide text-[10px]">{t("scoring.confidence", "Confidence")}</div><div className="font-semibold text-slate-800">{score.confidence}%</div></div>
          </div>
          {score.positive_factors?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-emerald-600 mb-1 flex items-center gap-1"><TrendUp size={12} /> {t("scoring.strengths", "Strengths")}</div>
              <ul className="space-y-1">
                {score.positive_factors.map((f, i) => (
                  <li key={i} data-testid={`${testid}-pos-${i}`} className="text-xs text-slate-600 flex gap-1.5"><span className="text-emerald-500">+</span>{factorLabel(t, f)}</li>
                ))}
              </ul>
            </div>
          )}
          {score.risk_factors?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-red-500 mb-1 flex items-center gap-1"><WarningCircle size={12} /> {t("scoring.risks", "Risks")}</div>
              <ul className="space-y-1">
                {score.risk_factors.map((f, i) => (
                  <li key={i} data-testid={`${testid}-risk-${i}`} className="text-xs text-slate-600 flex gap-1.5"><span className="text-red-400">−</span>{factorLabel(t, f)}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="bg-[#0B2545]/5 rounded-sm px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{t("scoring.nextAction", "Recommended action")}</div>
            <div className="text-xs font-medium text-[#0B2545] mt-0.5" data-testid={`${testid}-action`}>
              {score.next_action_text || t(`scoring.action.${score.next_action_code}`, score.next_action_code)}
            </div>
          </div>
          <div className="text-[10px] text-slate-400 italic">{t("scoring.rulesBased", "Rule-based score — not an AI prediction.")}</div>
        </div>
      )}
    </div>
  );
}
